import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer, Card, Button, Badge } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { useAuth0 } from '@aimsos/auth';
import { Shield, Sparkles, SlidersHorizontal, Users, Database, Inbox, ArrowRight } from 'lucide-react';

const ShellDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth0();
  const { t } = useTranslation('core-shell');

  const appCards = useMemo(
    () => [
      {
        titleKey: 'dashboard.governance.title',
        descKey: 'dashboard.governance.description',
        Icon: Shield,
        path: '/governance',
        badgeVariant: 'success',
        color: 'text-blue-600 dark:text-blue-400',
        bg: 'bg-blue-50 dark:bg-blue-900/20',
      },
      {
        titleKey: 'dashboard.agenticStudio.title',
        descKey: 'dashboard.agenticStudio.description',
        Icon: Sparkles,
        path: '/agentic-studio',
        badgeVariant: 'success',
        color: 'text-emerald-600 dark:text-emerald-400',
        bg: 'bg-emerald-50 dark:bg-emerald-900/20',
      },
      {
        titleKey: 'dashboard.dataStudio.title',
        descKey: 'dashboard.dataStudio.description',
        Icon: Database,
        path: '/data-studio',
        badgeVariant: 'success',
        color: 'text-cyan-600 dark:text-cyan-400',
        bg: 'bg-cyan-50 dark:bg-cyan-900/20',
      },
      {
        titleKey: 'dashboard.userManagement.title',
        descKey: 'dashboard.userManagement.description',
        Icon: Users,
        path: '/users',
        badgeVariant: 'success',
        color: 'text-purple-600 dark:text-purple-400',
        bg: 'bg-purple-50 dark:bg-purple-900/20',
      },
      {
        titleKey: 'dashboard.workQueues.title',
        descKey: 'dashboard.workQueues.description',
        Icon: Inbox,
        path: '/work-queues',
        badgeVariant: 'success',
        color: 'text-rose-600 dark:text-rose-400',
        bg: 'bg-rose-50 dark:bg-rose-900/20',
      },
      {
        titleKey: 'dashboard.adminStudio.title',
        descKey: 'dashboard.adminStudio.description',
        Icon: SlidersHorizontal,
        path: '/admin-studio',
        badgeVariant: 'success',
        color: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-50 dark:bg-amber-900/20',
      },
    ],
    [],
  );

  const displayName = user?.given_name || user?.name || 'there';

  return (
    <PageContainer title={t('dashboard.welcomeBack', { name: displayName })}>
      <p className="mb-8 text-content-muted">{t('dashboard.subtitle')}</p>

      <div className="grid gap-6 sm:grid-cols-2">
        {appCards.map((app) => (
          <Card
            key={app.path}
            className="cursor-pointer transition-shadow hover:shadow-md"
            onClick={() => navigate(app.path)}
          >
            <Card.Body>
              <div className="flex items-start gap-4">
                <div className={['rounded-xl p-3', app.bg].join(' ')}>
                  <app.Icon size={28} className={app.color} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-semibold text-content">{t(app.titleKey)}</h2>
                    <Badge variant={app.badgeVariant}>{t('dashboard.badgeActive')}</Badge>
                  </div>
                  <p className="mt-1 text-sm text-content-muted">{t(app.descKey)}</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    rightIcon={ArrowRight}
                    className="mt-3 -ml-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(app.path);
                    }}
                  >
                    {t('dashboard.openModule')}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    </PageContainer>
  );
};

export default ShellDashboard;
